import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HotelContext } from '../context/hotel';
import { CheckInContext } from '../context/checkin';
import { UserContext } from '../context/user';
import { useKiosk } from '../hooks/useKiosk';
import { useLanguage } from '../hooks/useLanguage';
import KioskEventProcessingOverlay from './KioskEventProcessingOverlay';

/**
 * Handler para el evento 'webcheckin-start'
 * Identifica al huésped y lo redirige a la pantalla apropiada
 */
const handleWebcheckinStart = async (lastEvent, { identify, setLanguageByCode, hotel, navigate, languageCode }) => {
  try {
    const result = await identify(lastEvent?.entityId);
    
    if (result) {

      // Establecer el idioma si está disponible
      if (result.language) {
        const langCode = languageCode(result.language);
        if (langCode && hotel.languages?.includes(langCode)) {
          setLanguageByCode(langCode);
        }
      }

      // Si el huésped está pendiente o observado, se redirige a la pantalla de edición de check-in
      if (result.status === 'pending' || result.status === 'observed') {
        navigate(`/${hotel.code}/checkin/edit-new`, { replace: true });
      } else {
        // Si el huésped no está pendiente o observado, se redirige a la pantalla de bienvenida del Kiosk
        navigate(`/${hotel.code}/kiosk/welcome-kiosk`, { replace: true });
      }
    } else {
      console.error('No se pudo identificar al huésped');
    }
  } catch (error) {
    console.error('Error procesando evento webcheckin-start:', error);
  }
};

/**
 * Handler para el evento 'kiosk-reload'
 * Se encarga de recargar la página
 */
const handleKioskAdminRemoteReload = ( lastEvent ) => {
  console.log('2feb handleKioskAdminRemoteReload', lastEvent);
  // con el lastEvent hay que identificar que tablet hay que recargar
  window.location.reload();
}
/**
 * Handler para el evento 'kiosk-admin-login'
 * Se encarga de setear en 1 el isKioskLoggedIn
 */
const handleKioskAdminRemoteLogin = ( lastEvent, { setLoginKiosk } ) => {
  console.log('2feb handleKioskAdminRemoteLogin', lastEvent);
  // con el lastEvent hay que identificar que tablet hay que loguear
  setLoginKiosk();
}

/**
 * Handler para el evento 'kiosk-admin-logout'
 * Se encarga de setear en 0 el isKioskLoggedIn
 */
const handleKioskAdminRemoteLogout = ( lastEvent, { setLogoutKiosk } ) => {
  console.log('2feb handleKioskAdminRemoteLogout', lastEvent);
  // con el lastEvent hay que identificar que tablet hay que desloguear
  setLogoutKiosk();
}

const eventHandlers = {
  'webcheckin-start': handleWebcheckinStart,
  'kiosk-admin-reload': handleKioskAdminRemoteReload,
  'kiosk-admin-logout': handleKioskAdminRemoteLogout,
  'kiosk-admin-login': handleKioskAdminRemoteLogin,
};

/**
 * GlobalKioskEventListener
 * Componente global que escucha eventos de Kiosk sin importar en qué pantalla se encuentre la aplicación.
 * Maneja múltiples tipos de eventos de forma escalable.
 */
const GlobalKioskEventListener = () => {
  const navigate = useNavigate();
  const { data: hotel } = useContext(HotelContext);
  const { identify } = useContext(CheckInContext);
  const { setLanguageByCode } = useContext(UserContext);
  const { isKioskMode, lastEvent, clearLastEvent, isProcessingEvent, setIsProcessingEvent, setLoginKiosk, setLogoutKiosk } = useKiosk();
  const { languageCode } = useLanguage();

  useEffect(() => {
    const handleKioskEvent = async () => {
      if (!isKioskMode || !lastEvent) return;

      console.log('2feb handleKioskEvent', lastEvent);
      // Obtener el handler correspondiente al topic del evento
      const handler = eventHandlers[lastEvent.topic];

      if (handler) {
        try {
          // Marcar que estamos procesando un evento
          setIsProcessingEvent(true);

          // Ejecutar el handler correspondiente
          await handler(lastEvent, {
            identify,
            setLanguageByCode,
            hotel,
            navigate,
            languageCode,
            setLoginKiosk,
            setLogoutKiosk
          });
        } catch (error) {
          console.error(`Error procesando evento ${lastEvent.topic}:`, error);
        } finally {
          // Limpiar el último evento después de procesarlo
          clearLastEvent();
          // Marcar que terminamos de procesar
          setIsProcessingEvent(false);
        }
      } else {
        console.warn(`No hay handler registrado para el evento: ${lastEvent.topic}`);
        clearLastEvent();
      }
    };

    handleKioskEvent();
  }, [hotel.code, isKioskMode, lastEvent]);

  return (
    <>
      {/* Overlay que aparece mientras se procesa un evento */}
      {isKioskMode && isProcessingEvent && <KioskEventProcessingOverlay />}
    </>
  );
};

export default GlobalKioskEventListener;
